import { adminAccessArea } from './admin-access-target';
/**
 * anyActionGuard — `CanActivateFn` factory gating a route on a SET of
 * actions with ANY-OF semantics: the route activates when the viewer holds
 * AT LEAST ONE of them.
 *
 * RESPONSIBILITY: `WorkspaceItem.actionKeys` in `core/constants/navigation.ts`
 * already carries ANY-OF person-level gating, but it governs what the More
 * menu SHOWS. It is not a gate — a pasted URL, a bookmark or a link in a
 * message never passes through the menu, so a workspace whose only person-
 * level gate is its registry entry is reachable by anyone the capability
 * flag lets through. This is the deep-link half, and the two are meant to
 * carry the same list.
 *
 * WHY A REDIRECT AND NOT A REFUSAL PAGE: it matches `featureFlagGuard` and
 * `buildingAlertManagementGuard`, both of which send an unentitled visitor
 * to `/home-preview` rather than confirming the surface exists.
 *
 * NOT A SUBSTITUTE FOR THE SERVER. Hierarchical matching runs against the
 * viewer's own resolved actions and decides what to render; every mutation
 * behind the route is re-checked by `@RequireAction` on the API. Getting
 * this list wrong makes a workspace unreachable, never unprotected.
 *
 * USAGE:
 *   {
 *     path: 'live-events',
 *     canActivate: [
 *       authGuard,
 *       featureFlagGuard('broadcast.playlist'),
 *       anyActionGuard(['live-event:manage', 'live-event:broadcast']),
 *     ],
 *   }
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { ProfileDataService } from '../services/profile-data.service';
import { ViewerService } from '../services/viewer.service';

export function anyActionGuard(actions: readonly string[]): CanActivateFn {
  return async (route) => {
    const viewer = inject(ViewerService);
    const profile = inject(ProfileDataService);
    const router = inject(Router);

    // The actions arrive with the viewer, and a guard that read them before
    // the fetch settled would bounce every hard reload of a legitimate
    // deep link. Same await, same reason, as `featureFlagGuard`.
    const loadedViewer = await viewer.ensureLoaded();

    if (actions.some((action) => profile.canDo(action))) return true;
    // `route?.data?.` — the router always supplies both, but the guard is
    // also called directly from specs with a bare `{}` snapshot, and a
    // refusal path that throws on the way to explaining itself is worse than
    // the bounce it replaced.
    const area = adminAccessArea(route?.data?.['accessStateArea']);
    if (area) return router.createUrlTree(['/platform/access-status'], { queryParams: {
      area, reason: loadedViewer ? 'permission' : 'unavailable',
    } });
    return router.createUrlTree(['/home-preview']);
  };
}
